import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

import { useGetEncounterQuery } from '@features/encounters/store';
import { usePatientDocumentReferences } from '@features/patient-artifacts/store';
import { getNoteRoute } from '@utils/noteRouting';

const CLINICAL_NOTE = 'clinical_note';

/**
 * Canonical encounter-open resolver. Resolves an encounter to its primary
 * output and hands off to the right viewer:
 *   - native note (encounter.note_id) -> /note/{id}
 *   - one migrated clinical-note doc   -> /document/{docref_id}
 *   - zero or many outputs             -> a list rendered here
 *
 * `/note/{id}` is unchanged — this only routes encounters that have no Mongo
 * note (migrated) to their document, so the encounter's note is openable the
 * way a native one is.
 */
export function EncounterOutputsRoute() {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();

  const {
    data: encounter,
    isLoading: encounterLoading,
    isError: encounterError,
  } = useGetEncounterQuery({ encounterId: encounterId ?? '' }, { skip: !encounterId });

  const patientId = encounter?.patient_id ?? '';
  const { items: docs, isLoading: docsLoading } = usePatientDocumentReferences({
    patientId,
    encounterId,
    skip: !encounter || Boolean(encounter.note_id) || !patientId,
  });

  // Memoized so the reference is stable across renders (a bare `.filter`
  // returns a fresh array each render, which would re-run the effect below).
  const clinicalNotes = useMemo(() => docs.filter(d => d.doc_kind === CLINICAL_NOTE), [docs]);

  useEffect(() => {
    if (!encounter) return;
    if (encounter.note_id) {
      navigate(getNoteRoute({ note_id: encounter.note_id, status: encounter.status }), {
        replace: true,
      });
      return;
    }
    if (docsLoading) return;
    if (clinicalNotes.length === 1) {
      navigate(`/document/${clinicalNotes[0].document_reference_id}`, {
        replace: true,
      });
    }
  }, [encounter, docsLoading, clinicalNotes, navigate]);

  if (encounterError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="error">Could not load this encounter.</Typography>
      </Box>
    );
  }

  const resolving =
    encounterLoading ||
    !encounter ||
    Boolean(encounter.note_id) ||
    (docsLoading && !encounter?.note_id) ||
    clinicalNotes.length === 1;

  if (resolving) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Zero or multiple outputs: list them; each opens its viewer.
  return (
    <Box sx={{ maxWidth: 720, minWidth: 0, overflowWrap: 'anywhere', mx: 'auto', p: 3 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h6" gutterBottom>
        Encounter documents
      </Typography>
      {docs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No documents for this encounter.
        </Typography>
      ) : (
        // Intentionally every document linked to the encounter, not just
        // clinical notes — this is the encounter's full documents list.
        <List>
          {docs.map(d => (
            <ListItemButton
              key={d.document_reference_id}
              onClick={() => navigate(`/document/${d.document_reference_id}`)}
            >
              <ListItemText
                primary={d.title || d.doc_kind || 'Document'}
                secondary={d.authored_at ?? undefined}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
}
